import React, { useMemo, useState } from 'react';
import { Box, FormField, Link, MixedLineBarChart, Multiselect, Popover, Select, SpaceBetween, Toggle } from '@cloudscape-design/components';
import { buildOperationsChart, formatTrendValue } from './operationsTrendsChart';

const { MEASURES } = require('../../lib/operationsTrends');
const CHART_LABELS = {
  funding: 'Funding by EI / CRF',
  applications: 'Applications received',
  reviewDays: 'Time in review',
  interventionDays: 'Intervention duration',
  signatureDays: 'Time to sign forms',
};
const chartOption = measure => ({ value: measure.value, label: CHART_LABELS[measure.value] || measure.label });
const CHART_OPTIONS = [
  { label: 'Funding', topic: 'funding' },
  { label: 'Applications', topic: 'applications' },
  { label: 'Interventions', topic: 'interventions' },
  { label: 'Outcomes', topic: 'results' },
].map(group => ({
  label: group.label,
  options: MEASURES.filter(measure => measure.topic === group.topic).map(chartOption),
}));
const ControlInfo = ({ title, ariaLabel, children }) => (
  <Popover size="large" header={title} content={children} renderWithPortal>
    <Link variant="info" ariaLabel={ariaLabel || `About ${title}`}>Info</Link>
  </Popover>
);

export default function OperationsTrends({ report, fiscalYearStart, monthIndexes, staff, region, loading }) {
  const [measure, setMeasure] = useState(MEASURES[0]);
  const [showTrend, setShowTrend] = useState(false);
  const [selection, setSelection] = useState(null);
  const changeMeasure = next => {
    setMeasure(next);
    setShowTrend(false);
    setSelection(null);
  };
  const chart = useMemo(() => buildOperationsChart({ trends: report?.trends, measure, fiscalYearStart, monthIndexes, staff, region, showTrend }), [report, measure, fiscalYearStart, monthIndexes, staff, region, showTrend]);
  // Report filters change the data, not the user's chart choices. Retain IDs
  // even when a category temporarily has no rows; changing Chart resets them.
  const selectedIds = selection ?? chart.defaultSeriesIds;
  const seriesOptions = chart.series.filter(series => !series.isTrend).map(series => ({ value: series.id, label: series.title }));
  const selectedOptions = seriesOptions.filter(option => selectedIds.includes(option.value));
  const visibleSeries = chart.series.filter(series => series.isTrend || selectedIds.includes(series.id));
  const changeSelection = ids => setSelection(ids);
  const format = value => formatTrendValue(value, measure.unit);
  const maximum = Math.max(1, ...visibleSeries.flatMap(series => series.data.map(point => point.y)));
  const trendAvailable = measure.unit !== 'days';
  const renderedSeries = chart.hasData ? visibleSeries : [];
  return <div style={{ height: '100%', overflowY: 'auto' }}><SpaceBetween size="m">
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))', gap: 12 }}>
      <FormField label="Chart" stretch info={<ControlInfo title={chartOption(measure).label} ariaLabel="About the chart">{measure.description}</ControlInfo>}>
        <Select ariaLabel="Chart" selectedOption={chartOption(measure)} options={CHART_OPTIONS} expandToViewport
          filteringType="auto" filteringPlaceholder="Find a chart" filteringAriaLabel="Find a chart"
          empty="No charts available" noMatch="No charts match your search"
          onChange={({ detail }) => changeMeasure(MEASURES.find(entry => entry.value === detail.selectedOption.value))} />
      </FormField>
      <FormField label="Show" stretch info={<ControlInfo title="Show">Choose a combined view or individual items to compare.</ControlInfo>}>
        <Multiselect ariaLabel="Show" selectedOptions={selectedOptions} options={seriesOptions} expandToViewport
          filteringType="auto" filteringPlaceholder="Find an item" filteringAriaLabel="Find an item"
          placeholder={selectedOptions.length ? `${selectedOptions.length} of ${seriesOptions.length} selected` : 'Choose items'} empty="No items for this period" noMatch="No items match your search"
          tokenLimit={2} inlineTokens keepOpen enableSelectAll
          deselectAriaLabel={option => `Hide ${option.label}`}
          i18nStrings={{ selectAllText: 'Select all', tokenLimitShowMore: 'Show more', tokenLimitShowFewer: 'Show fewer' }}
          onChange={({ detail }) => changeSelection(detail.selectedOptions.map(option => option.value))} />
      </FormField>
      <FormField label="Trend" stretch info={<ControlInfo title="Show trend">See the overall direction over time. Available for monthly amounts and counts.</ControlInfo>}>
        <div style={{ display: 'flex', alignItems: 'center', minHeight: 32 }}>
          <Toggle checked={showTrend && trendAvailable} disabled={!trendAvailable} onChange={({ detail }) => setShowTrend(detail.checked)}>Show trend</Toggle>
        </div>
      </FormField>
    </div>
    <MixedLineBarChart
      ariaLabel="Operations trends"
      statusType={loading ? 'loading' : 'finished'}
      series={renderedSeries}
      visibleSeries={renderedSeries}
      onFilterChange={({ detail }) => changeSelection(detail.visibleSeries.filter(series => !series.isTrend).map(series => series.id))}
      xDomain={chart.xDomain}
      yDomain={[0, Math.ceil(maximum * 1.15)]}
      xScaleType="categorical"
      xTitle="Month"
      yTitle={measure.unit === 'cad' ? 'Canadian dollars' : measure.unit === 'days' ? 'Median calendar days' : 'Count'}
      height={220}
      hideFilter
      legendTitle={chartOption(measure).label}
      i18nStrings={{ xTickFormatter: value => value, yTickFormatter: format, legendAriaLabel: 'Chart legend', chartAriaRoleDescription: 'Monthly operations bar and line chart' }}
      detailPopoverSeriesContent={({ series, x, y }) => {
        const point = series.data.find(entry => entry.x === x);
        return { key: series.title, value: `${format(y)}${measure.unit === 'days' ? ` days (n=${point?.samples || 0})` : ''}${x === chart.partialLabel ? ' · month in progress' : ''}` };
      }}
      empty={<Box textAlign="center" color="text-status-inactive">{chart.hasData && !visibleSeries.length ? 'Choose an item in Show to draw the chart.' : 'No recorded data for this chart and period.'}</Box>}
    />
  </SpaceBetween></div>;
}
